import { useCallback, useMemo, useState } from 'react'
import {
  useReactTable,
  getPaginationRowModel,
  getCoreRowModel,
  getFilteredRowModel,
} from '@tanstack/react-table'
import * as XLSX from 'xlsx'
import { reportService } from '@/app/services/reportService'
import { format } from 'date-fns'
import { FiltersType } from '../components/FilterReports'
import { TableReportCustomersColumnsDefinition } from './components/TableReportCustomersDefinition'
import { useReportCustomers } from '@/app/hooks/useReportCustomers'
import {
  ReportCustomers,
  ReportCustomersParams,
} from '@/app/services/reportService/reportCustomers'
import { formatMoneyValue } from '@/app/utils/formatMoneyValue'
import masks from '@/app/utils/masks'
import { fetchAllReportData } from '@/app/utils/fetchAllReportData'
import { useToast } from '@/view/components/ui/use-toast'

const AMOUNT_REPORTS_FETCH = Number(import.meta.env.VITE_AMOUNT_REPORTS_FETCH)

export const useReportClientController = () => {
  const [exportProgress, setExportProgress] = useState(0)
  const [isExporting, setIsExporting] = useState(false)
  const { data, isFetching, isLoading, setParams, params } =
    useReportCustomers()
  const { toast } = useToast()

  const { columns } = TableReportCustomersColumnsDefinition()

  const table = useReactTable({
    data: data?.customers || [],
    columns,
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      pagination: {
        pageIndex: 0,
        pageSize: data?.pagination.limit || 100,
      },
    },
  })

  const fetchAllData = useCallback(async (): Promise<ReportCustomers[]> => {
    if (!data) return []

    setIsExporting(true)
    setExportProgress(0)

    try {
      return await fetchAllReportData<ReportCustomers>({
        total: data.pagination.total,
        batchSize: AMOUNT_REPORTS_FETCH,
        onProgress: setExportProgress,
        fetchBatch: async ({ skip, limit }) => {
          const exportParams: ReportCustomersParams = { ...params, limit, skip }
          const batchData = await reportService.reportCustomers(exportParams)
          return batchData.customers
        },
      })
    } finally {
      setIsExporting(false)
    }
  }, [data, params])

  const handleExportToExcel = useCallback(async () => {
    try {
      const fetchedData = await fetchAllData()

      const formattedData = fetchedData.map((clients) => ({
        name: clients.name || '',
        email: clients.email,
        cpf: masks.cpf(clients.cpf) || '',
        phone: masks.phone(clients.phone) || 0,
        amount: formatMoneyValue(clients.available.amount),
        transaction: clients.transactions,
        transactionsAmount: formatMoneyValue(clients.total),
        lastTransaction: clients.lastTransaction
          ? format(clients.lastTransaction, 'dd/MM/yyyy, HH:mm')
          : '-',
        date: format(clients.createdAt, 'dd/MM/yyyy, HH:mm') || '',
      }))

      if (formattedData.length > 0) {
        const workbook = XLSX.utils.book_new()
        const worksheet = XLSX.utils.json_to_sheet(formattedData)
        XLSX.utils.book_append_sheet(workbook, worksheet, 'ExportedReport')
        XLSX.utils.sheet_add_aoa(worksheet, [
          [
            'Nome',
            'Email',
            'Cpf',
            'Telefone',
            'Saldo',
            'Transações',
            'Transações(R$)',
            'Data da transação',
            'Data do cadastro',
          ],
        ])
        XLSX.writeFile(workbook, 'clients-reports.xlsx')
      }
    } catch (error) {
      console.error('Error exporting clients report:', error)
      toast({
        title: 'Não foi possível exportar o relatório',
        description:
          'Alguns dados não puderam ser carregados. Tente novamente.',
        variant: 'destructive',
      })
    } finally {
      setIsExporting(false)
    }
  }, [fetchAllData, toast])

  const getPageIndex = () => {
    if (!data) return 0
    return Math.floor(data.pagination.skip / data.pagination.limit) + 1
  }

  const getPageCount = () => {
    if (!data) return 0
    return Math.ceil(data.pagination.total / data.pagination.limit)
  }

  const handleNextPage = () => {
    if (!data) return
    const newPage = data.pagination.skip / data.pagination.limit + 1
    if (getPageCount() === getPageIndex()) return
    const skipValue = newPage * data.pagination.limit
    setParams((p) => ({ ...p, skip: skipValue }))
  }

  const handlePreviousPage = () => {
    if (!data) return
    const newPage = data.pagination.skip / data.pagination.limit - 1
    if (newPage < 0) return
    const skipValue = newPage * data.pagination.limit
    setParams((p) => ({ ...p, skip: skipValue }))
  }

  const filters: FiltersType[] = useMemo(() => {
    const result: FiltersType[] = [
      'Date',
      'TransactionDates',
      'SortByCustomerStatus',
    ]

    return result
  }, [])
  return {
    table,
    filters,
    handleNextPage,
    handlePreviousPage,
    getPageIndex,
    getPageCount,
    isFetching,
    isLoading,
    setParams,
    isExporting,
    exportProgress,
    handleExportToExcel,
  }
}
